import React, { useState, useEffect } from 'react';
import { getUsers, User, updateUserClaimsTier, getStats } from '../../api/admin';

const ClaimsTierManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'none' | 'free' | 'premium'>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tierStats, setTierStats] = useState<{ [key: string]: number }>({});

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch users
      const response = await getUsers(1, 100, search);
      let filteredUsers = response.users;

      if (filter !== 'all') {
        filteredUsers = filteredUsers.filter(u => u.claims_tier === filter);
      }

      setUsers(filteredUsers);

      // Fetch statistics
      const stats = await getStats();
      setTierStats(stats.claims_tiers);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter, search]);

  const handleUpdateTier = async (email: string) => {
    try {
      await updateUserClaimsTier(email);
      fetchData(); // Refresh the list
    } catch (err) {
      setError(`Failed to update claims tier for ${email}`);
      console.error(err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const getTierBadgeClass = (tier: string) => {
    switch (tier) {
      case 'premium':
        return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      case 'free':
        return 'badge-info';
      case 'none':
        return 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30';
    }
  };

  const getNextTier = (currentTier: string) => {
    switch (currentTier) {
      case 'none':
        return 'free';
      case 'free':
        return 'premium';
      case 'premium':
        return 'none';
      default:
        return 'none';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-zinc-400">Loading claims tiers...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 text-zinc-100">Claims Tier Management</h2>

        {/* Statistics Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="stat-card border-l-4 border-l-zinc-500">
            <div className="stat-card-label">None</div>
            <div className="stat-card-value text-zinc-400">{tierStats.none || 0}</div>
          </div>
          <div className="stat-card border-l-4 border-l-blue-500">
            <div className="stat-card-label">Free</div>
            <div className="stat-card-value text-blue-400">{tierStats.free || 0}</div>
          </div>
          <div className="stat-card border-l-4 border-l-purple-500">
            <div className="stat-card-label">Premium</div>
            <div className="stat-card-value text-purple-400">{tierStats.premium || 0}</div>
          </div>
          <div className="stat-card border-l-4 border-l-emerald-500">
            <div className="stat-card-label">Total Users</div>
            <div className="stat-card-value text-emerald-400">
              {(tierStats.none || 0) + (tierStats.free || 0) + (tierStats.premium || 0)}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4 flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email..."
            className="input-dark flex-1 px-4 py-2 rounded-lg"
          />
          <button
            type="submit"
            className="btn-primary px-4 py-2 rounded-lg"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="btn-secondary px-4 py-2 rounded-lg"
            >
              Clear
            </button>
          )}
        </form>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'all'
                ? 'bg-amber-500 text-zinc-900'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            All Tiers
          </button>
          <button
            onClick={() => setFilter('none')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'none'
                ? 'bg-zinc-500 text-white'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            None
          </button>
          <button
            onClick={() => setFilter('free')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'free'
                ? 'bg-blue-500 text-white'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            Free
          </button>
          <button
            onClick={() => setFilter('premium')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'premium'
                ? 'bg-purple-500 text-white'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            Premium
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            {error}
          </div>
        )}

        {/* Info Box */}
        <div className="alert alert-info mb-4">
          <p className="text-sm">
            <strong>Tier Progression:</strong> Click "Update Tier" to cycle through tiers: None → Free → Premium → None
          </p>
        </div>

        {/* Claims Tier List */}
        <div className="card-dark rounded-xl overflow-hidden">
          <table className="table-dark">
            <thead>
              <tr>
                <th>Email</th>
                <th>Current Tier</th>
                <th>Subscription Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium text-zinc-200">
                    {user.email}
                  </td>
                  <td>
                    <span className={`badge ${getTierBadgeClass(user.claims_tier)}`}>
                      {user.claims_tier}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        user.subscription_active
                          ? 'badge-success'
                          : 'badge-danger'
                      }`}
                    >
                      {user.subscription_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleUpdateTier(user.email)}
                      className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                    >
                      Update Tier → {getNextTier(user.claims_tier)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClaimsTierManager;
